/**
 * ExcesMoto.ro — interacțiuni „Pit Lane" peste Horizon.
 *
 * - „Motocicleta mea": vehicle finder salvat în localStorage (cheia `em_moto`)
 * - Fitment personalizat pe produs și pe carduri
 * - Copiere cod OEM, galerie + zoom, variantă, bară sticky
 * - Adăugare în coș AJAX integrată cu cart drawer-ul Horizon
 * - Filtre colecție (auto-submit), formular „Cere piesă" + link WhatsApp live
 */

const STORAGE_KEY = 'em_moto';

const norm = (value) => String(value ?? '').toLowerCase().trim();

function getMoto() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return value && value.marca && value.model ? value : null;
  } catch (_) {
    return null;
  }
}

function setMoto(moto) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(moto));
  } catch (_) {
    /* stocare indisponibilă (mod privat) — ignorăm */
  }
}

const motoLabel = (moto) => (moto ? `${moto.marca} ${moto.model}` : '');

/* ------------------------------------------------------------------ */
/* „Motocicleta mea" — etichete                                        */
/* ------------------------------------------------------------------ */
function renderMotoLabels(root = document) {
  const moto = getMoto();
  root.querySelectorAll('[data-em-moto-label]').forEach((el) => {
    el.textContent = moto ? motoLabel(moto) : el.dataset.empty || '';
  });
  root.querySelectorAll('[data-em-moto-show]').forEach((el) => {
    el.hidden = !moto;
  });
}

/* ------------------------------------------------------------------ */
/* Vehicle finder (hero)                                               */
/* ------------------------------------------------------------------ */
function initFinder(finder) {
  if (finder.dataset.emReady) return;
  finder.dataset.emReady = '1';

  let data = [];
  try {
    data = JSON.parse(finder.querySelector('[data-em-finder-data]')?.textContent || '[]');
  } catch (_) {
    data = [];
  }

  const brandSelect = finder.querySelector('[data-em-finder-brand]');
  const modelSelect = finder.querySelector('[data-em-finder-model]');
  const go = finder.querySelector('[data-em-finder-go]');
  const saved = finder.querySelector('[data-em-finder-saved]');
  if (!brandSelect || !modelSelect || !go) return;

  const placeholder = modelSelect.querySelector('option')?.textContent || '';

  const fillModels = (brand, selectedLabel) => {
    modelSelect.innerHTML = '';
    modelSelect.add(new Option(placeholder, ''));
    data.forEach((item, index) => {
      if (item.brand !== brand) return;
      const option = new Option(item.label, String(index));
      if (selectedLabel && item.label === selectedLabel) option.selected = true;
      modelSelect.add(option);
    });
    modelSelect.disabled = !brand;
  };

  const current = () => (modelSelect.value === '' ? null : data[Number(modelSelect.value)] || null);

  const updateGo = () => {
    const item = current();
    if (item?.url) go.href = item.url;
  };

  const flagError = (el) => {
    el.classList.add('is-error');
    el.focus();
    setTimeout(() => el.classList.remove('is-error'), 1600);
  };

  brandSelect.addEventListener('change', () => {
    fillModels(brandSelect.value);
    updateGo();
  });

  modelSelect.addEventListener('change', updateGo);

  go.addEventListener('click', (event) => {
    const item = current();
    if (!item) {
      event.preventDefault();
      flagError(brandSelect.value ? modelSelect : brandSelect);
      return;
    }
    setMoto({ marca: item.brand, model: item.label, cod: item.cod, tag: item.tag, url: item.url });
  });

  const moto = getMoto();
  if (moto && data.some((item) => item.brand === moto.marca)) {
    brandSelect.value = moto.marca;
    fillModels(moto.marca, moto.model);
    updateGo();
  }
  if (saved) saved.hidden = !moto;
}

/* ------------------------------------------------------------------ */
/* Fitment pe pagina de produs                                         */
/* ------------------------------------------------------------------ */
function renderFitment(root = document) {
  const moto = getMoto();

  root.querySelectorAll('[data-em-fitment]').forEach((el) => {
    const tags = (el.dataset.tags || '').split('|').map(norm).filter(Boolean);
    const universal = el.dataset.universal === 'true';
    let state = 'none';
    let message = el.dataset.msgNone || '';

    if (universal) {
      state = 'ok';
      message = el.dataset.msgUniversal || '';
    } else if (moto && moto.tag && tags.includes(norm(moto.tag))) {
      state = 'ok';
      message = el.dataset.msgOk || '';
    } else if (moto) {
      state = 'warn';
      message = el.dataset.msgWarn || '';
    }

    el.classList.remove('em-fit--ok', 'em-fit--warn', 'em-fit--none');
    el.classList.add(`em-fit--${state}`);
    el.textContent = message.replace('{moto}', motoLabel(moto));

    if ((state === 'none' || state === 'warn') && el.dataset.finderUrl) {
      const link = document.createElement('a');
      link.href = el.dataset.finderUrl;
      link.textContent = state === 'none' ? el.dataset.finderLabel || 'Alege motocicleta →' : 'Schimbă motocicleta';
      el.append(' · ', link);
    }
  });
}

/* ------------------------------------------------------------------ */
/* Badge fitment pe carduri                                            */
/* ------------------------------------------------------------------ */
function renderCardFit(root = document) {
  const moto = getMoto();
  if (!moto || !moto.tag) return;
  const tag = norm(moto.tag);

  root.querySelectorAll('[data-em-card]').forEach((card) => {
    const badge = card.querySelector('[data-em-card-fit]');
    if (!badge || !badge.hidden) return;
    const tags = (card.dataset.fitTags || '').split('|');
    if (tags.includes(tag)) {
      badge.textContent = `✓ ${moto.cod || moto.model}`;
      badge.hidden = false;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Copiere cod OEM                                                     */
/* ------------------------------------------------------------------ */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (_) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.append(area);
    area.select();
    document.execCommand('copy');
    area.remove();
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-em-copy]') : null;
  if (!button) return;
  const original = button.dataset.label || button.textContent;
  button.dataset.label = original;
  await copyText(button.dataset.emCopy || '');
  button.textContent = '✓ Copiat';
  button.classList.add('is-copied');
  clearTimeout(button._emTimer);
  button._emTimer = setTimeout(() => {
    button.textContent = original;
    button.classList.remove('is-copied');
  }, 1600);
});

/* ------------------------------------------------------------------ */
/* Deschidere cart drawer din header                                   */
/* ------------------------------------------------------------------ */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-em-cart-toggle]') : null;
  if (!trigger) return;
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    if (drawer.isOpen && typeof drawer.close === 'function') drawer.close();
    else drawer.open();
  }
});

/* ------------------------------------------------------------------ */
/* Galerie produs + zoom                                               */
/* ------------------------------------------------------------------ */
function initGallery(gallery) {
  if (gallery.dataset.emReady) return;
  gallery.dataset.emReady = '1';

  const main = gallery.querySelector('[data-em-main-img]');
  const thumbs = Array.from(gallery.querySelectorAll('[data-em-thumb]'));
  const zoomButton = gallery.querySelector('[data-em-zoom]');
  const dialog = gallery.querySelector('[data-em-zoom-dialog]');
  const zoomPrev = gallery.querySelector('[data-em-zoom-prev]');
  const zoomNext = gallery.querySelector('[data-em-zoom-next]');

  // Preîncarcă în fundal restul pozelor (mărime galerie + mărime zoom) ca
  // trecerea la poza următoare/miniatură să fie instant, nu abia la click.
  function preloadGalleryImages() {
    thumbs.forEach((thumb) => {
      if (thumb.dataset.src) {
        const img = new Image();
        img.src = thumb.dataset.src;
      }
      if (thumb.dataset.full) {
        const full = new Image();
        full.src = thumb.dataset.full;
      }
    });
  }
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(preloadGalleryImages, { timeout: 2000 });
  } else {
    setTimeout(preloadGalleryImages, 1000);
  }

  function activateThumb(thumb) {
    if (!main || !thumb) return;
    thumbs.forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute('aria-pressed', 'false');
    });
    thumb.classList.add('is-active');
    thumb.setAttribute('aria-pressed', 'true');
    main.removeAttribute('srcset');
    main.src = thumb.dataset.src;
    if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
    main.alt = thumb.dataset.alt || '';
    main.dataset.full = thumb.dataset.full || thumb.dataset.src;
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => activateThumb(thumb));
  });

  function showInDialog(thumb) {
    if (!dialog || !thumb) return;
    const image = dialog.querySelector('img');
    if (image) {
      image.src = thumb.dataset.full || thumb.dataset.src;
      image.alt = thumb.dataset.alt || '';
    }
  }

  function goToOffset(offset) {
    if (thumbs.length === 0) return;
    let idx = thumbs.findIndex((item) => item.classList.contains('is-active'));
    if (idx === -1) idx = 0;
    idx = (idx + offset + thumbs.length) % thumbs.length;
    const thumb = thumbs[idx];
    activateThumb(thumb);
    showInDialog(thumb);
  }

  if (zoomButton && dialog && main && typeof dialog.showModal === 'function') {
    zoomButton.addEventListener('click', () => {
      const image = dialog.querySelector('img');
      if (image) {
        image.src = main.dataset.full || main.currentSrc || main.src;
        image.alt = main.alt;
      }
      dialog.showModal();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || (event.target instanceof Element && event.target.closest('[data-em-zoom-close]'))) {
        dialog.close();
      }
    });
    if (zoomPrev) {
      zoomPrev.addEventListener('click', (event) => {
        event.stopPropagation();
        goToOffset(-1);
      });
    }
    if (zoomNext) {
      zoomNext.addEventListener('click', (event) => {
        event.stopPropagation();
        goToOffset(1);
      });
    }
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') goToOffset(-1);
      else if (event.key === 'ArrowRight') goToOffset(1);
    });
  }
}

/* ------------------------------------------------------------------ */
/* Selector variantă (pentru produse cu mai multe variante)            */
/* ------------------------------------------------------------------ */
function initVariantSelect(select) {
  if (select.dataset.emReady) return;
  select.dataset.emReady = '1';

  select.addEventListener('change', () => {
    const option = select.selectedOptions[0];
    if (!option) return;
    const available = option.dataset.available === 'true';

    document.querySelectorAll('[data-em-price]').forEach((el) => {
      el.textContent = option.dataset.price || el.textContent;
    });
    document.querySelectorAll('[data-em-atc-btn]').forEach((button) => {
      button.disabled = !available;
      button.textContent = available ? button.dataset.labelAdd : button.dataset.labelSoldout;
    });

    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    window.history.replaceState(null, '', url.toString());
  });
}

/* ------------------------------------------------------------------ */
/* Adăugare în coș (AJAX) — compatibilă cu evenimentele Horizon         */
/* ------------------------------------------------------------------ */
const routes = () => window.Theme?.routes || {};

async function fetchCart() {
  const response = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-em-atc-form]')) return;
  event.preventDefault();

  const buttons = document.querySelectorAll('[data-em-atc-btn]');
  const errorBox = document.querySelector('[data-em-atc-error]');
  buttons.forEach((button) => button.classList.add('is-loading'));
  if (errorBox) errorBox.hidden = true;

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity') || 1);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  let events = null;
  let deferred = null;
  try {
    events = await import('@shopify/events');
    deferred = events.CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );
  } catch (_) {
    deferred = null;
  }

  const resolveWith = async (payload, didError) => {
    if (!deferred || !events) return;
    try {
      const cart = await fetchCart();
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'em-product-form',
          itemCount: quantity,
          productId: form.dataset.productId,
          sections: payload?.sections,
          didError,
        },
      });
    } catch (error) {
      deferred.reject(error);
    }
  };

  try {
    const response = await fetch(routes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const payload = await response.json();

    if (payload.status) {
      await resolveWith(null, true);
      throw new Error(payload.description || payload.message || 'Nu am putut adăuga produsul în coș.');
    }

    await resolveWith(payload, false);

    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      setTimeout(() => {
        if (!drawer.isOpen) drawer.open();
      }, 60);
    } else {
      window.location.href = routes().cart_url || '/cart';
    }
  } catch (error) {
    if (errorBox) {
      errorBox.textContent = error?.message || 'Nu am putut adăuga produsul în coș. Încearcă din nou.';
      errorBox.hidden = false;
    }
  } finally {
    buttons.forEach((button) => button.classList.remove('is-loading'));
  }
});

/* ------------------------------------------------------------------ */
/* Bară sticky produs                                                  */
/* ------------------------------------------------------------------ */
function initSticky(root = document) {
  const bar = root.querySelector('[data-em-sticky]');
  const target = document.querySelector('[data-em-buttons]');
  if (!bar || bar.dataset.emReady) return;
  bar.dataset.emReady = '1';

  if (target && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(target);
  }

  bar.querySelector('[data-em-sticky-atc]')?.addEventListener('click', () => {
    const form = document.querySelector('[data-em-atc-form]');
    if (form instanceof HTMLFormElement) form.requestSubmit();
  });
}

/* ------------------------------------------------------------------ */
/* Filtre colecție                                                     */
/* ------------------------------------------------------------------ */
function initFilters(root = document) {
  const form = root.querySelector('[data-em-filter-form]');
  if (form && !form.dataset.emReady) {
    form.dataset.emReady = '1';

    const submit = () => {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (value !== '') params.append(key, String(value));
      }
      const query = params.toString();
      window.location.href = form.action + (query ? `?${query}` : '');
    };

    document.querySelectorAll('[data-em-autosubmit]').forEach((el) => el.addEventListener('change', submit));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit();
    });
  }

  const details = root.querySelector('[data-em-filters]');
  if (details && !details.dataset.emReady) {
    details.dataset.emReady = '1';
    const desktop = window.matchMedia('(min-width: 990px)');
    const sync = () => {
      if (desktop.matches) details.open = true;
      else if (!details.dataset.touched) details.open = false;
    };
    details.querySelector('summary')?.addEventListener('click', () => {
      details.dataset.touched = '1';
    });
    sync();
    desktop.addEventListener('change', sync);
  }
}

/* ------------------------------------------------------------------ */
/* Formular „Cere piesă"                                               */
/* ------------------------------------------------------------------ */
function initRequest(form) {
  if (form.dataset.emReady) return;
  form.dataset.emReady = '1';
  form.noValidate = true;

  const field = (name) => form.querySelector(`[data-em-field="${name}"]`);
  const waLink = document.querySelector('[data-em-request-wa]');
  const errorBox = form.querySelector('[data-em-request-error]');

  const updateWa = () => {
    if (!waLink) return;
    const value = (name, fallback) => field(name)?.value.trim() || fallback;
    const template = waLink.dataset.template || 'Bună ziua! Caut o piesă: {piesa} pentru {marca} {model} {an}.';
    const message = template
      .replace('{piesa}', value('piesa', '[piesa]'))
      .replace('{marca}', value('marca', '[marca]'))
      .replace('{model}', value('model', '[model]'))
      .replace('{an}', value('an', '[an]'));
    waLink.href = `${waLink.dataset.base}?text=${encodeURIComponent(message)}`;
  };

  form.addEventListener('input', (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && target.classList.contains('is-error') && 'value' in target && target.value.trim()) {
      target.classList.remove('is-error');
    }
    updateWa();
  });
  form.addEventListener('change', updateWa);
  updateWa();

  form.addEventListener('submit', (event) => {
    const required = [...form.querySelectorAll('[data-em-required]')];
    const missing = required.filter((el) => !el.value.trim());
    required.forEach((el) => el.classList.toggle('is-error', missing.includes(el)));

    if (missing.length) {
      event.preventDefault();
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      missing[0].focus();
      return;
    }

    if (errorBox) errorBox.hidden = true;
    const email = form.querySelector('[data-em-email]');
    if (email && !email.value.trim()) email.value = email.dataset.fallback || '';
  });
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */
function init(root = document) {
  renderMotoLabels(root);
  root.querySelectorAll('[data-em-finder]').forEach(initFinder);
  renderFitment(root);
  renderCardFit(root);
  root.querySelectorAll('[data-em-gallery]').forEach(initGallery);
  root.querySelectorAll('[data-em-variant-select]').forEach(initVariantSelect);
  initSticky(root);
  initFilters(root);
  root.querySelectorAll('[data-em-request-form]').forEach(initRequest);
}

init();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) {
    renderMotoLabels();
    renderFitment();
    renderCardFit();
  }
});
